<template>
  <section>
    <div v-if="!isInitialLoading" class="flex justify-between items-center gap-2 mb-4 capitalize md:mb-[1.21rem]">
      <span v-if="title && ((storesList && storesList.length > 0) || isLoading)" class="flex flex-nowrap gap-2 md:gap-3 items-center">
        <!-- section title -->
        <SectionTitle class="px-2 sm:px-0 py-0" appendedClass="p-0"
          :title="title" />
          <!-- filter by verified stores only checkbox -->
          <StoreVerificationCheckbox v-if="designId == 1" @onChange="changeFilterByVerifiedStoresOnlyState" :isChecked="filterByVerifiedStoresOnly"/>
      </span>
      <!-- more link -->
      <NuxtLink v-if="designId == 1" :to="`${ROUTESNAMES.user.stores}${ filterByVerifiedStoresOnly ? '?verified=1' : ''}`" class="text-primaryLighter group cursor-pointer hover:brightness-125 whitespace-nowrap duration-150 transition-colors font-bold text-[0.8rem] xs:text-[0.9rem] md:text-[1rem] md:text-[1.1rem] flex flex-nowrap gap-0.5 md:gap-1 items-center">
        <span>{{ $t('general.more')}}</span>
        <svg class="ltr:group-hover:translate-x-[3px] rtl:group-hover:translate-x-[-3px] transition-[transform] duration-150 ltr:rotate-180 w-[19px] h-[19px] xs:w-[20px] xs:h-[20px] md:w-[23px] md:h-[23px]" xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 256 256"><path fill="currentColor" d="M168.49 199.51a12 12 0 0 1-17 17l-80-80a12 12 0 0 1 0-17l80-80a12 12 0 0 1 17 17L97 128Z"/></svg>
      </NuxtLink>
    </div>

    <div :class="`relative`">
      <!-- when loading -->
      <ul v-if="isLoading" class="flex w-full  flex-nowrap overflow-hidden items-center gap-2">
        <li v-for="(_, index) in Array.from({ length: 12 })" :key="index">
          <div role="status"
            class="flex store-item animate-pulse bg-gray-100 rounded-[0.5rem] flex-col p-3 w-[150px] mx-0.5 sm:mx-1 md:w-[157px] text-center items-center justify-center">
            <div
              class="w-[80px] image-container bg-gray-50 rounded-full dark:bg-gray-700 mb-4 select-none h-[80px] lg:w-[85px] lg:h-[85px] overflow-hidden" />
            <span
              class="text-[12px] w-full p-2 mb-1 bg-gray-50 h-[28px] text-center font-normal bg-[#0963FF29] text-[#2955A1] rounded-[150px]" />
          </div>
        </li>
      </ul>
      <!-- when loaded -->
      <div v-else-if="storesList && storesList.length > 0" class="w-full">
        <div class="custom-slider" ref="sliderContainer"
          :class="{ 'from-single-store': fromSingleStorePage, 'swipeable': isSwipeable }"
          :data-paused="isPaused"
          :data-dragging="isDragging"
          @mouseenter="pauseAutoplay"
          @mouseleave="resumeAutoplay">
          <div 
            class="slider-track" 
            ref="sliderTrack" 
            :class="{ 'from-single-store': fromSingleStorePage, 'swipeable': isSwipeable }"
            :style="trackStyle"
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
            @mousedown="handleTouchStart"
            @mousemove="handleTouchMove"
            @mouseup="handleTouchEnd"
            @mouseleave="handleTouchEnd"
          >
            <div 
              class="slider-item" 
              v-for="(store, index) in duplicatedStoresList" 
              :key="`${store.id}-${index}`"
              :style="{ width: itemWidth + 'px' }"
            >
              <StoreItem class="relative" :store="store"
                :usedCookieLanguage="usedCookieLanguage" :fromStorePage="fromStorePage" :storesType="storesType" :designCardId="3" :placeholderImg="placeholderImg"
                :openBranchModal="openBranchModal" :appendedClass="fromSingleStorePage ? 'w-[8rem] sm:w-[9rem] sm:mx-1 md:w-[9.170rem] lg:w-[8.270rem] 3xl:w-[9.810rem] rounded-[0.5rem] h-full max-h-[12.188rem] md:max-h-[12.5rem]' : ''" />
            </div>
          </div>
        </div>
      </div>
      <!-- store branch Modal -->
      <Teleport to="body">
        <div id="customConfirmationDialog" v-if="showStoreModal">
          <div @click="showStoreModal = false" :class="`modal-backdrop ${showStoreModal ? '' : 'hidden'}`"></div>
          <div id="popupModal" tabindex="-1">
            <!-- modal body -->
            <div class="fixed modal-inner z-[150] sm:w-full w-[90%] max-w-[40rem] h-fit">
              <div class="relative bg-white rounded-lg modal_inner_dialog">
                <!-- close button -->
                <button @click="showStoreModal = false" type="button"
                  class="absolute top-2 left-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center">
                  <svg aria-hidden="true" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd" />
                  </svg>
                  <span class="sr-only">Close modal</span>
                </button>
                <!--modal content -->
                <div class="sm:p-6 p-3 text-center">
                  <section :class="`  flex flex-col items-center mb-3 `">
                    <!-- store logo -->
                    <div
                      class="relative w-[100px] flex justify-center items-center bg-[#0963FF26] aspect-square rounded-[50%] mb-3">
                      <img v-if="storeData?.logo" @error="onImageError" loading="lazy"
                        class="w-full aspect-square rounded-[50%] object-cover" :src="storeData?.logo || placeholderImg"
                        :alt="storeData?.name" />
                      <img v-else @error="onImageError" class="w-full aspect-square rounded-[50%] object-cover"
                        :src="placeholderImg" alt />
                    </div>
                    <!-- store name -->
                    <h1 class="lg:text-[.9rem] sm:text-[.85rem] text-[.8rem] text-center text-[#000000] font-semibold">
                      {{ storeData?.name }}
                    </h1>
                    <!-- store phone -->
                    <div class="flex flex-nowrap gap-2">
                      <div :style="{ direction: 'ltr' }"
                        class="flex flex-nowrap text-[.8rem] font-bold py-1.5 px-3 sm:px-5 md:px-8 xl:px-9 gap-1.5 text-white rounded-[22px] bg-primary">
                        <img width="13" height="13" src="@/assets/store/call.svg" class="object-contain" alt />
                        <a :href="`tel:${storeData?.phone}`">
                          {{ storeData?.phone }}
                        </a>
                      </div>
                      <div v-if="storeData?.phone2" :style="{ direction: 'ltr' }"
                        class="flex flex-nowrap text-[.8rem] font-bold py-1.5 px-3 sm:px-5 md:px-8 xl:px-9 gap-1.5 text-white rounded-[22px] bg-primary">
                        <img width="13" height="13" src="@/assets/store/call.svg" class="object-contain" alt />
                        <a :href="`tel:${storeData?.phone2}`">
                          {{ storeData?.phone2 }}
                        </a>
                      </div>
                    </div>
                  </section>
                  <hr class="mb-3 border-dashed" />
                  <section :class="`  flex sm:flex-row  flex-col justify-between  items-center mb-2 `">
                    <div class="sm:self-start flex flex-col justify-between gap-4 mb-3">
                      <strong class="text-[#070707] lg:text-[.9rem] sm:text-[.85rem] text-[.8rem]">
                        {{ $t("user.branch_address") }}
                      </strong>
                      <p
                        class="lg:text-[.8rem] text-center sm:text-[.75rem] text-[.7rem] font-normal text-[#545454] multi-lines-text">
                        {{ storeData.address }}
                      </p>
                      <a v-if="storeData?.location" :href="formatedLocationLink"
                        class="lg:text-[.9rem] sm:text-[.85rem] text-[.8rem] mx-auto max-h-max rounded-[5px] text-white font-normal py-1.5 px-3 bg-primary hover:brightness-95 transition-all duration-150"
                        rel="noopener noreferrer" target="_blank">{{ $t("user.show_map") }}</a>
                    </div>
                    <div v-if="storeData?.location"
                      class="relative w-full flex justify-center sm:w-1/3 min-h-[100px] rounded-5" :style="{
      backgroundImage: `url(${mapImg})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
                      }"></div>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Teleport>
    </div>
  </section>
</template>

<script>
import SectionTitle from "../SectionTitle.vue";
import placeholderImg from "@/assets/images/shopping-lo.png";
import mapImg from "@/assets/store/cropped-google_map-1.png";
import { ROUTESNAMES } from "@/helpers";
import { StoreItem } from "@/components";
import StoreVerificationCheckbox from "../general/StoreVerificationCheckbox.vue";

let regexLocationPattern =
  /^https:\/\/www\.google\.com\/maps\?q=-?\d+(\.\d+)?,-?\d+(\.\d+)?$/;

export default {
  setup() {
    const usedCookieLanguage =
      useCookie("localStorageCopy")?.value?.language || "ar";
    return {
      usedCookieLanguage,
    };
  },
  data() {
    return {
      ROUTESNAMES,
      placeholderImg,
      showStoreModal: false,
      storeData: {},
      mapImg,
      currentSlidePerView: 8,
      itemWidth: 157,
      currentPosition: 0,
      animationFrameId: null,
      lastTimestamp: null,
      scrollSpeed: 20, // pixels per second
      isDragging: false,
      startX: 0,
      startPosition: 0,
      isPaused: false,
    };
  },
  components: {
    SectionTitle,
    StoreItem,
    StoreVerificationCheckbox,
  },
  props: {
    isInitialLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    storesList: {
      type: Array,
      required: true,
      default: () => ([]),
    },
    title: {
      type: String,
      required: false,
      default: "",
    },
    storesType: {
      type: String,
      required: false,
      default: "main",
    },
    loadMoreStores: {
      type: Function,
      required: false,
      default: () => ({}),
    },
    isLoadingMoreStores: {
      type: Boolean,
      required: false,
      default: false,
    },
    filterByVerifiedStoresOnly: {
      type: [Boolean, Number],
      required: false,
      default: 0,
    },
    changeFilterByVerifiedStoresOnlyState:{
      type: Function,
      required: false,
    },
    designId: {
      type: Number,
      required: false,
      default: 1,
    },
    fromStorePage: {
      type: Boolean,
      required: false,
      default: false,
    },
    fromSingleStorePage: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  methods: {
    openBranchModal(branchData){
      if(!branchData){
        return;
      }
      this.showStoreModal = true; 
      this.storeData = branchData;
    },
    startAutoplay() {
      this.stopAutoplay();
      // Only start autoplay if swipeable
      if (!this.isSwipeable) return;
      this.lastTimestamp = performance.now();
      this.animate();
    },
    stopAutoplay() {
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      this.lastTimestamp = null;
    },
    animate() {
      if (this.isPaused || this.isDragging || !this.isSwipeable) {
        this.lastTimestamp = null;
        return;
      }

      const currentTimestamp = performance.now();
      const deltaTime = this.lastTimestamp ? currentTimestamp - this.lastTimestamp : 16; // Default to ~60fps if no last timestamp
      this.lastTimestamp = currentTimestamp;

      // Calculate movement based on time delta for consistent speed across all frame rates
      const movement = (this.scrollSpeed * deltaTime) / 1000;
      this.currentPosition += movement;

      // Check for loop reset - cache calculations to avoid repeated work
      const totalWidth = this.duplicatedStoresList.length * this.itemWidth;
      const visibleWidth = this.currentSlidePerView * this.itemWidth;
      const maxPosition = totalWidth - visibleWidth;
      
      if (this.currentPosition >= maxPosition) {
        this.currentPosition = 0;
      }

      // Continue animation loop - use direct function reference for better performance
      this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
    },
    moveNext() {
      const totalWidth = this.duplicatedStoresList.length * this.itemWidth;
      const visibleWidth = this.currentSlidePerView * this.itemWidth;
      
      // Move very slowly for smooth continuous movement
      this.currentPosition += 1;
      
      // Reset to beginning when reaching the end
      if (this.currentPosition >= totalWidth - visibleWidth) {
        this.currentPosition = 0;
      }
    },
    handleTouchStart(e) {
      // Only handle touch if swiping is needed (regardless of fromSingleStorePage)
      if (!this.isSwipeable) return;
      
      this.isDragging = true;
      this.startX = e.touches ? e.touches[0].clientX : e.clientX;
      this.startPosition = this.currentPosition;
      this.stopAutoplay();
    },
    handleTouchMove(e) {
      if (!this.isDragging || !this.isSwipeable) return;
      
      const currentX = e.touches ? e.touches[0].clientX : e.clientX;
      const diff = this.startX - currentX;
      const direction = this.isRightToLeft ? -1 : 1;
      
      this.currentPosition = this.startPosition + (diff * direction);
    },
    handleTouchEnd() {
      // Only handle touch end if swiping is needed (regardless of fromSingleStorePage)
      if (!this.isSwipeable) return;
      
      this.isDragging = false;
      
      // Snap to nearest item with smooth transition
      const snappedPosition = Math.round(this.currentPosition / this.itemWidth) * this.itemWidth;
      
      // Apply smooth transition for snapping
      const track = this.$refs.sliderTrack;
      if (track) {
        track.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        this.currentPosition = snappedPosition;
        // Remove transition after animation completes
        setTimeout(() => {
          if (track) {
            track.style.transition = 'none';
          }
        }, 300);
      } else {
        this.currentPosition = snappedPosition;
      }
      
      // Restart autoplay only if it's needed
      if (this.isSwipeable) {
        this.startAutoplay();
      }
    },
    calculateSlidesPerView() {
      setTimeout(() => {
        const itemFullWidth = 120;
        const itemFullWidthOnMobile = 100;
        const slideWidth = window.innerWidth >= 480 ? itemFullWidth : itemFullWidthOnMobile;
        const containerWidth = this.$refs.sliderContainer?.getBoundingClientRect()?.width;

        if (containerWidth) {
          this.currentSlidePerView = Math.floor(containerWidth / slideWidth);
          this.itemWidth = slideWidth;
        }
      }, 10);
    },
    pauseAutoplay() {
      this.stopAutoplay();
      // Add visual feedback that slider is paused
      this.isPaused = true;
    },
    resumeAutoplay() {
      // Only resume if not currently dragging
      if (!this.isDragging) {
        this.isPaused = false;
        // Restart animation if it was stopped
        if (!this.animationFrameId && this.isSwipeable) {
          this.startAutoplay();
        }
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.calculateSlidesPerView();
      // Always start autoplay when component mounts
      this.startAutoplay();
    });
    window.addEventListener('resize', this.calculateSlidesPerView);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calculateSlidesPerView);
    this.stopAutoplay();
  },
  computed: {
    isRightToLeft() {
      return this.usedCookieLanguage === "ar";
    },
    duplicatedStoresList() {
      // Create multiple copies of the stores list for seamless infinite looping
      if (!this.storesList || this.storesList.length === 0) return [];
      
      // Check if swiping is needed first
      const containerWidth = this.$refs.sliderContainer?.getBoundingClientRect()?.width || 0;
      const totalItemsWidth = this.storesList.length * this.itemWidth;
      const needsSwiping = totalItemsWidth > containerWidth;
      
      // If swiping is needed, always duplicate the list (regardless of fromSingleStorePage)
      if (needsSwiping) {
        // Duplicate the list 6 times to ensure smooth infinite looping
        const duplicated = [];
        for (let i = 0; i < 6; i++) {
          duplicated.push(...this.storesList);
        }
        return duplicated;
      }
      
      // If no swiping is needed, return the original list
      console.log('No swiping needed. Returning original list:', this.storesList.length);
      return this.storesList;
    },
    isSwipeable() {
      const containerWidth = this.$refs.sliderContainer?.getBoundingClientRect()?.width || 0;
      const totalItemsWidth = this.storesList.length * this.itemWidth;
      const isSwipeable = totalItemsWidth > containerWidth;
      
      
      // Check if items overflow the container (cards disappear behind the section)
      // This works for both fromSingleStorePage true and false
      // If items overflow, swiping is needed regardless of the page type
      return isSwipeable;
    },
    trackStyle() {
      // If not swipeable, don't apply any transform (regardless of fromSingleStorePage)
      if (!this.isSwipeable) {
        return {
          transform: 'none',
          transition: 'none'
        };
      }
      
      const direction = this.isRightToLeft ? 1 : -1;
      // Round position to avoid sub-pixel rendering issues
      const roundedPosition = Math.round(this.currentPosition * direction * 100) / 100;
      return {
        // Use translate3d for better GPU acceleration
        transform: `translate3d(${roundedPosition}px, 0, 0)`,
        // Remove transition during animation - only use when dragging ends
        transition: this.isDragging ? 'none' : 'none'
      };
    },
    formatedLocationLink() {
      if (this.storeData.location) {
        if (regexLocationPattern.test(this.storeData.location)) {
          //check if it came as a link or lat,lng
          return this.storeData.location;
        } else {
          let coordinatesArr = this.storeData.location.split(",");
          return `https://maps.google.com/maps?q=${coordinatesArr[0]},${coordinatesArr[1]}`;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.arrow-shadow {
  filter: drop-shadow(0 0 10px rgba(0, 0, 0, 0.3));
}

.store-item {
  flex: 0 0 160px;

  @media only screen and (max-width: 670px) {
    flex: 0 0 150px;
  }
}

/* Custom Slider Styles */
.custom-slider {
  overflow: hidden;
  position: relative;
  width: 100%;
  /* Add smooth transition for hover effects */
  transition: all 0.3s ease;
}

/* When fromSingleStorePage is true but not swipeable, remove overflow hidden to show all items */
.custom-slider.from-single-store:not(.swipeable) {
  overflow: visible;
}

.custom-slider:hover {
  /* Subtle visual feedback when hovering */
  cursor: grab;
}

.custom-slider:active {
  cursor: grabbing;
}

.slider-track {
  display: flex;
  gap: 1.5rem; /* Maintain the same gap as original */
  cursor: grab;
  /* GPU acceleration for smoother movement */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}

.slider-track:active {
  cursor: grabbing;
}

/* When fromSingleStorePage is true but not swipeable, disable transform and touch interactions */
.slider-track.from-single-store:not(.swipeable) {
  will-change: auto;
  transform: none !important;
  transition: none !important;
  cursor: default;
}

/* Optimize image rendering in slider items */
.slider-item .image-container {
  /* Optimize image rendering */
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
}

.slider-item {
  flex-shrink: 0;
  /* Remove any dots or unwanted elements */
  list-style: none;
  /* Add smooth transitions for hover effects */
  transition: all 0.2s ease;
  /* Isolate repaints to prevent flickering */
  contain: layout style paint;
  /* GPU acceleration */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}

/* Enhanced hover effects for store cards - disabled during dragging */
.custom-slider:not([data-dragging="true"]) .slider-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

/* Disable hover effects when slider is actively moving */
.custom-slider[data-dragging="true"] .slider-item:hover {
  transform: none;
  box-shadow: none;
}

/* Remove any dots or bullets from store items */
.slider-item::before,
.slider-item::after {
  content: none !important;
  display: none !important;
}

/* Touch interaction improvements */
.slider-track {
  touch-action: pan-x;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  /* Remove any list styling */
  list-style: none;
  padding: 0;
  margin: 0;
}

/* Smooth transitions - removed during animation for better performance */
.slider-track {
  /* No transition during animation - handled by requestAnimationFrame */
  transition: none;
  transform-style: preserve-3d;
  /* Remove backface-visibility: hidden to prevent flickering */
  backface-visibility: visible;
  -webkit-backface-visibility: visible;
}

/* Optimize will-change usage - only when actively animating */
.custom-slider:not([data-paused="true"]):not([data-dragging="true"]) .slider-track {
  will-change: transform;
}

/* Remove will-change when paused or dragging to optimize performance */
.custom-slider[data-paused="true"] .slider-track,
.custom-slider[data-dragging="true"] .slider-track {
  will-change: auto;
}

/* Ensure smooth infinite loop */
.slider-track {
  animation: none;
  transform-origin: center center;
}

/* Pause indicator - subtle visual feedback */
.custom-slider[data-paused="true"] .slider-track {
  opacity: 0.95;
}

/* Manual swipe feedback */
.custom-slider[data-dragging="true"] .slider-track {
  transition: none;
  cursor: grabbing;
}

/* Enhanced touch interaction */
.slider-track {
  touch-action: pan-x;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  /* Remove any list styling */
  list-style: none;
  padding: 0;
  margin: 0;
}

/* Keep existing Swiper styling for compatibility */
.mySwiper {
  .swiper-slide {
    width: auto;
    height: auto;
  }
  
  .swiper-wrapper {
    align-items: stretch;
  }
}

/* Ensure the same gap spacing as ItemsSlider gap="1.5" */
.swiper-slide {
  margin-right: 1.5rem;
}

.swiper-slide:last-child {
  margin-right: 0;
}
</style>
